import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  IconButton,
  Typography,
  TextField,
  Menu,
} from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import {
  MoreVert as MoreVertIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  useClientInfo,
  axiosInstance,
  SnackbarAlert,
} from "../../utils/commonImports";
import { useLocation, useNavigate } from "react-router-dom";
import BookingFormAdd from "./BookingFormAdd"; // Add/Edit form component
import { useConfirmation } from "../../hooks/useConfirmation";
import dayjs from "dayjs";
import MyDatePicker from "../../components/MyDatePicker";
import { useAppContext } from "../../context/AppContext";


const FILTERS_STATUS = [
  "All",
  "Pending",
  "TS Generated",
  "Processing",
  "Completed",
  "Cancelled",
];

const FILTERS_DUTYTYPE = ["All", "Local", "Outstation", "Employee Transfer"];

export default function BookingForm() {
 const { clientId } = useClientInfo();
const { fyid } = useAppContext();
const location = useLocation();
  const navigate = useNavigate();

  const tableRef = useRef();
  const [savedScroll, setSavedScroll] = useState(0);
  const autoOpenedRef = useRef(false);

  // Data
  const [bookings, setBookings] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [vehicleGroups, setVehicleGroups] = useState([]);

  // Filters
  const [filterStatus, setFilterStatus] = useState("All");
  const [filterDutyType, setFilterDutyType] = useState("All");
  const [filterCustomer, setFilterCustomer] = useState("");
  const [filterVehicle, setFilterVehicle] = useState("");
  const [filterVehicleType, setFilterVehicleType] = useState("");
  const [filterVehicleGroup, setFilterVehicleGroup] = useState("");
  const [filterFromDate, setFilterFromDate] = useState("");
  const [filterToDate, setFilterToDate] = useState("");

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Edit/Add form states
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);

  // Menu & Snackbar
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    msg: "",
    severity: "success",
  });
  const [confirm, ConfirmationDialogComponent] = useConfirmation();

  // Fetch bookings
    const fetchBookings = useCallback(async () => {
    if (!clientId || !fyid) return;
    try {
      const params = new URLSearchParams();
      params.append("client_id", clientId);
      params.append("fyid", fyid);
      if (filterStatus !== "All") params.append("booking_status", filterStatus);
      if (filterDutyType !== "All") params.append("duty_type", filterDutyType);
      if (filterCustomer) params.append("customer_id", filterCustomer);
      if (filterVehicle) params.append("vehicle_id", filterVehicle);
      if (filterVehicleType) params.append("vehicle_type", filterVehicleType);
      if (filterVehicleGroup)
        params.append("vehicle_group_id", filterVehicleGroup);

      if (filterFromDate) {
        const fromIso = dayjs(filterFromDate, [
          "YYYY-MM-DD",
          "DD/MM/YYYY",
        ]).format("YYYY-MM-DD");
        params.append("from_date", fromIso);
      }
      if (filterToDate) {
        const toIso = dayjs(filterToDate, ["YYYY-MM-DD", "DD/MM/YYYY"]).format(
          "YYYY-MM-DD",
        );
        params.append("to_date", toIso);
      }

      const res = await axiosInstance.get(`/api/bookings?${params}`, {
        headers: { client_id: clientId },
      });
      setBookings(res.data);
    } catch (err) {
      setSnackbar({
        open: true,
        msg: "Failed to fetch bookings.",
        severity: "error",
      });
    }
  }, [
    clientId,
      fyid,
    filterStatus,
    filterDutyType,
    filterCustomer,
    filterVehicle,
    filterVehicleGroup,
    filterVehicleType,
    filterFromDate,
    filterToDate,
  ]);

  // Fetch dropdowns
  const fetchDropdowns = useCallback(async () => {
    if (!clientId) return;
    try {
      const [custRes, vehRes, vtRes, vgRes] = await Promise.all([
        axiosInstance.get("/api/customers", {
          params: { client_id: clientId },
        }),
        axiosInstance.get("/api/vehicles", { params: { client_id: clientId } }),
        axiosInstance.get("/api/components/category", {
          params: { type: "VehicleType", client_id: clientId },
        }),
        axiosInstance.get("/api/components/category", {
          params: { type: "VehicleGroup", client_id: clientId },
        }),
      ]);

      setCustomers(custRes.data || []);
      setVehicles(vehRes.data || []);
      setVehicleTypes(vtRes.data || []);
      setVehicleGroups(vgRes.data || []);
    } catch (err) {
      console.error("Failed to fetch dropdown data", err);
    }
  }, [clientId]);

  useEffect(() => {
    fetchDropdowns();
  }, [fetchDropdowns]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings, location.key]);

  useEffect(() => {
    if (!showForm && tableRef.current) {
      tableRef.current.scrollTop = savedScroll;
    }
  }, [showForm, savedScroll]);

  const getStatusStyle = (status) => {
    switch (status) {
      case "Pending":
        return {
          backgroundColor: "#F59E0B",
          color: "#FFFFFF",
        };

      case "TS Generated":
        return {
          backgroundColor: "#2563EB",
          color: "#FFFFFF",
        };

      case "Processing":
        return {
          backgroundColor: "#7C3AED",
          color: "#FFFFFF",
        };

      case "Completed":
        return {
          backgroundColor: "#2E7D32",
          color: "#FFFFFF",
        };

      case "Cancelled":
        return {
          backgroundColor: "#6B7280",
          color: "#FFFFFF",
        };

      default:
        return {
          backgroundColor: "#6B7280",
          color: "#FFFFFF",
        };
    }
  };

  // Pagination handlers
  const handleChangePage = (_event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Menu handlers
  const handleRowMenuOpen = (event, row) => {
    setAnchorEl(event.currentTarget);
    setMenuRow(row);
  };

  const handleRowMenuClose = () => {
    setAnchorEl(null);
    setMenuRow(null);
  };

  // Delete Booking
  const handleDelete = async (row) => {
    handleRowMenuClose();
    if (!(await confirm("Are you sure you want to delete this booking?")))
      return;
    try {
      await axiosInstance.delete(
        `/api/bookings/${row.bookingid}?client_id=${clientId}`,
      );
      setSnackbar({ open: true, msg: "Booking deleted", severity: "success" });
      fetchBookings();
    } catch (err) {
      setSnackbar({ open: true, msg: "Delete failed", severity: "error" });
    }
  };

  // Cancel Booking
  const handleCancelBooking = async (row) => {
    handleRowMenuClose();

    if (!row) return;

    // Do not allow cancelling already cancelled booking
    if (
      String(row.calculated_status || "").toLowerCase() === "cancelled" ||
      String(row.booking_status || "").toLowerCase() === "cancelled"
    ) {
      SnackbarAlert("Booking is already cancelled", "warning");
      return;
    }

    // Do not allow cancelling Processing / Completed
    const currentStatus = row.calculated_status || "";

    if (currentStatus === "Processing" || currentStatus === "Completed") {
      SnackbarAlert(
        `Booking cannot be cancelled because it is ${currentStatus}.`,
        "warning",
      );
      return;
    }

    const confirmed = await confirm(
      `Are you sure you want to cancel booking ${row.booking_no}?`,
    );

    if (!confirmed) return;

    try {
      await axiosInstance.put(`/api/bookings/${row.bookingid}/cancel`, {
        client_id: clientId,
      });

      setSnackbar({
        open: true,
        msg: "Booking cancelled successfully",
        severity: "success",
      });

      fetchBookings();
    } catch (err) {
      console.error("Cancel booking failed:", err?.response?.data || err);

      setSnackbar({
        open: true,
        msg: err?.response?.data?.message || "Failed to cancel booking",
        severity: "error",
      });
    }
  };

  // Edit Booking
  const handleEdit = async (row) => {
    try {
      if (tableRef.current) setSavedScroll(tableRef.current.scrollTop);
      const res = await axiosInstance.get(
        `/api/bookings/${row.bookingid}?client_id=${clientId}`,
      );
      setEditData(res.data);
      setShowForm(true);
    } catch (err) {
      setSnackbar({
        open: true,
        msg: "Failed to fetch booking details",
        severity: "error",
      });
    }
    handleRowMenuClose();
  };

  // ============================================================
  // GENERATE TRIPSHEET FROM BOOKING
  // ============================================================
  // const handleGenerateTripsheet = (row) => {
  //   handleRowMenuClose();

  //   if (!row?.bookingid) {
  //     SnackbarAlert("Booking ID missing", "error");
  //     return;
  //   }

  //   // Only Pending bookings can generate a Tripsheet
  //   if (row.calculated_status !== "Pending") {
  //     SnackbarAlert(
  //       "Tripsheet can only be generated for Pending bookings",
  //       "warning",
  //     );
  //     return;
  //   }

  //   // Open TripsheetFormAdd and pass Booking ID
  //   navigate("/tripsheet/add", {
  //     state: { bookingId: row.bookingid, fromBooking: true },
  //   });
  // };

  // ============================================================
  // GENERATE TRIPSHEET FROM BOOKING
  // ============================================================
  const handleGenerateTripsheet = (row) => {
    handleRowMenuClose();

    if (!row?.bookingid) {
      SnackbarAlert("Booking ID missing", "error");
      return;
    }

    // Only Pending bookings can generate a Tripsheet
    if (row.calculated_status !== "Pending") {
      SnackbarAlert(
        "Tripsheet can only be generated for Pending bookings",
        "warning",
      );
      return;
    }

    console.log("🚕 Generate Tripsheet");
    console.log("Booking ID:", row.bookingid);

    // ============================================================
    // OPEN TRIPSHEET FORM DIRECTLY
    // ============================================================
    navigate("/tripsheets/tripsheet/add", {
      state: {
        bookingId: row.bookingid,
        fromBooking: true,
        accounting_type:
          row.accounting_type === "Estimation" ? "Estimation" : "Confirmed",
      },
    });
  };

  // Filter Reset
  const resetFilters = () => {
    setFilterStatus("All");
    setFilterDutyType("All");
    setFilterCustomer("");
    setFilterVehicle("");
    setFilterVehicleGroup("");
    setFilterVehicleType("");
    setFilterFromDate("");
    setFilterToDate("");
  };

  // Sliced rows for pagination
  const pagedRows = bookings.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return (
    <Box sx={{ width: "100%", minHeight: "100vh", p: 1, bgcolor: "#f8fafc" }}>
      {/* FILTERS & ADD */}
      {!showForm && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap", // Allow filters to wrap on small screens
            gap: 0.5, // Reduced gap for tight spacing
            justifyContent: "start",
            mb: 1,
            mt: 1,

            "& .MuiInputLabel-root": { fontSize: "0.70rem" },
            "& .MuiSelect-root": { fontSize: "0.70rem" },
            "& .MuiMenuItem-root": { fontSize: "0.70rem" },
            "& .MuiButton-root": { fontSize: "0.70rem" },
            "& .MuiMenu-list": { fontSize: "0.70rem" },
          }}
        >
          {/* Booking Status */}
          <FormControl
            size="small"
            sx={{ minWidth: 140, flexGrow: 1, maxWidth: 220 }}
          >
            <InputLabel>Booking Status</InputLabel>
            <Select
              value={filterStatus}
              label="Booking Status"
              onChange={(e) => setFilterStatus(e.target.value)}
              MenuProps={{ PaperProps: { sx: { fontSize: "0.70rem" } } }}
            >
              {FILTERS_STATUS.map((val) => (
                <MenuItem key={val} value={val} sx={{ fontSize: "0.70rem" }}>
                  {val}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Duty Type */}
          <FormControl
            size="small"
            sx={{ minWidth: 140, flexGrow: 1, maxWidth: 220 }}
          >
            <InputLabel>Duty Type</InputLabel>
            <Select
              value={filterDutyType}
              label="Duty Type"
              onChange={(e) => setFilterDutyType(e.target.value)}
              MenuProps={{ PaperProps: { sx: { fontSize: "0.70rem" } } }}
            >
              {FILTERS_DUTYTYPE.map((val) => (
                <MenuItem key={val} value={val} sx={{ fontSize: "0.70rem" }}>
                  {val}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Customer */}
          <FormControl
            size="small"
            sx={{ minWidth: 160, flexGrow: 1, maxWidth: 280 }}
          >
            <InputLabel>Customer</InputLabel>
            <Select
              value={filterCustomer}
              label="Customer"
              onChange={(e) => setFilterCustomer(e.target.value)}
              MenuProps={{ PaperProps: { sx: { fontSize: "0.70rem" } } }}
            >
              <MenuItem value="" sx={{ fontSize: "0.70rem" }}>
                All
              </MenuItem>
              {customers.map((c) => (
                <MenuItem
                  key={c.customerid}
                  value={c.customerid}
                  sx={{ fontSize: "0.70rem" }}
                >
                  {c.customername}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Vehicle */}
          <FormControl
            size="small"
            sx={{ minWidth: 160, flexGrow: 1, maxWidth: 280 }}
          >
            <InputLabel>Vehicle</InputLabel>
            <Select
              value={filterVehicle}
              label="Vehicle"
              onChange={(e) => setFilterVehicle(e.target.value)}
              MenuProps={{ PaperProps: { sx: { fontSize: "0.70rem" } } }}
            >
              <MenuItem value="" sx={{ fontSize: "0.70rem" }}>
                All
              </MenuItem>
              {vehicles.map((v) => (
                <MenuItem
                  key={v.vehicle_id}
                  value={v.vehicle_id}
                  sx={{ fontSize: "0.70rem" }}
                >
                  {v.vehicle_no || v.vehicle_number}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Vehicle Group */}
          <FormControl
            size="small"
            sx={{ minWidth: 160, flexGrow: 1, maxWidth: 280 }}
          >
            <InputLabel>Vehicle Group</InputLabel>
            <Select
              value={filterVehicleGroup}
              label="Vehicle Group"
              onChange={(e) => setFilterVehicleGroup(e.target.value)}
              MenuProps={{ PaperProps: { sx: { fontSize: "0.70rem" } } }}
            >
              <MenuItem value="" sx={{ fontSize: "0.70rem" }}>
                All
              </MenuItem>
              {vehicleGroups.map((vg) => (
                <MenuItem
                  key={vg.categoryid}
                  value={vg.categoryid}
                  sx={{ fontSize: "0.70rem" }}
                >
                  {vg.categoryname}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* From Date */}
          <MyDatePicker
            label="From Date"
            value={filterFromDate}
            onChange={(date) => setFilterFromDate(date)}
            inputStyle={{
              fontSize: "0.70rem",
              minHeight: 28,
              padding: "4px 8px",
            }}
            labelStyle={{ fontSize: "0.70rem", lineHeight: 1, marginBottom: 2 }}
            containerStyle={{ width: 140, minHeight: 32 }}
          />

          {/* To Date */}
          <MyDatePicker
            label="To Date"
            value={filterToDate}
            onChange={(date) => setFilterToDate(date)}
            inputStyle={{
              fontSize: "0.70rem",
              minHeight: 28,
              padding: "4px 8px",
            }}
            labelStyle={{ fontSize: "0.70rem", lineHeight: 1, marginBottom: 2 }}
            containerStyle={{ width: 140, minHeight: 32 }}
          />

          {/* Reset Filters */}
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={resetFilters}
            sx={{
              minWidth: 100,
              fontSize: "0.70rem",
              height: 32,
              minHeight: 32,
              lineHeight: "1rem",
              padding: "0 12px",
              boxSizing: "border-box",
            }}
          >
            RESET FILTERS
          </Button>

          {/* Add Booking */}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="small"
            onClick={() => {
              setEditData(null);
              setShowForm(true);
            }}
            sx={{
              minWidth: 80,
              fontSize: "0.70rem",
              height: 32,
              minHeight: 32,
              lineHeight: "1rem",
              padding: "0 1px",
              boxSizing: "border-box",
            }}
          >
            ADD
          </Button>
        </Box>
      )}

      {/* LIST TABLE */}
      {showForm ? (
        <BookingFormAdd
          editData={editData}
          onCancel={() => setShowForm(false)}
          onSuccess={() => {
            setShowForm(false);
            setSnackbar({
              open: true,
              msg: "Booking saved",
              severity: "success",
            });
            fetchBookings();
          }}
        />
      ) : (
        <Paper
          elevation={2}
          sx={{ borderRadius: 2, overflowX: "auto" }}
          ref={tableRef}
        >
          <Table size="small" sx={{ minWidth: 1100, fontSize: "0.50rem" }}>
            <TableHead>
              <TableRow style={{ backgroundColor: "#f5f5f5", height: 32 }}>
                <TableCell
                  sx={{
                    width: 100,
                    fontSize: "0.80rem",
                    fontWeight: "bold",
                    color: "#888",
                  }}
                >
                  Booking No
                </TableCell>
                <TableCell
                  sx={{
                    width: 200,
                    fontSize: "0.80rem",
                    fontWeight: "bold",
                    color: "#888",
                  }}
                >
                  Customer
                </TableCell>
                <TableCell
                  sx={{
                    width: 120,
                    fontSize: "0.80rem",
                    fontWeight: "bold",
                    color: "#888",
                  }}
                >
                  Vehicle
                </TableCell>
                <TableCell
                  sx={{
                    width: 140,
                    fontSize: "0.80rem",
                    fontWeight: "bold",
                    color: "#888",
                  }}
                >
                  Vehicle Group
                </TableCell>
                <TableCell
                  sx={{
                    width: 90,
                    fontSize: "0.80rem",
                    fontWeight: "bold",
                    color: "#888",
                  }}
                >
                  Duty Type
                </TableCell>
                <TableCell
                  sx={{
                    width: 100,
                    fontSize: "0.80rem",
                    fontWeight: "bold",
                    color: "#888",
                  }}
                >
                  Reporting Date
                </TableCell>
                <TableCell
                  sx={{
                    width: 100,
                    fontSize: "0.80rem",
                    fontWeight: "bold",
                    color: "#888",
                  }}
                >
                  Release Date
                </TableCell>
                <TableCell
                  sx={{
                    width: 100,
                    fontSize: "0.80rem",
                    fontWeight: "bold",
                    color: "#888",
                  }}
                >
                  Booking Status
                </TableCell>
                <TableCell
                  sx={{
                    width: 70,
                    fontSize: "0.80rem",
                    fontWeight: "bold",
                    textAlign: "center",
                    color: "#888",
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody sx={{ fontSize: "0.60rem" }}>
              {pagedRows.length > 0 ? (
                pagedRows.map((row) => {
                  const customer = customers.find(
                    (c) => Number(c.customerid) === Number(row.customer_id),
                  );
                  const vehicle = vehicles.find(
                    (v) => Number(v.vehicle_id) === Number(row.vehicle_id),
                  );
                  const vehicleGroup = vehicleGroups.find(
                    (vg) =>
                      Number(vg.categoryid) === Number(row.vehicle_group_id),
                  );

                  return (
                    <TableRow key={row.bookingid} hover>
                      <TableCell sx={{ width: 100 }}>
                        {row.booking_no}
                      </TableCell>
                      <TableCell sx={{ width: 200, fontSize: "0.80rem" }}>
                        {customer ? customer.customername : "-"}
                      </TableCell>
                      <TableCell sx={{ width: 120, fontSize: "0.80rem" }}>
                        {vehicle
                          ? vehicle.vehicle_no || vehicle.vehicle_number || "-"
                          : "-"}
                      </TableCell>
                      <TableCell sx={{ width: 140, fontSize: "0.80rem" }}>
                        {vehicleGroup ? vehicleGroup.categoryname : "-"}
                      </TableCell>
                      <TableCell sx={{ width: 90, fontSize: "0.80rem" }}>
                        {row.duty_type}
                      </TableCell>
                      <TableCell sx={{ width: 100, fontSize: "0.80rem" }}>
                        {row.reporting_date
                          ? new Date(row.reporting_date).toLocaleDateString(
                              "en-GB",
                            )
                          : "-"}
                      </TableCell>
                      <TableCell sx={{ width: 100, fontSize: "0.80rem" }}>
                        {row.release_date
                          ? new Date(row.release_date).toLocaleDateString(
                              "en-GB",
                            )
                          : "-"}
                      </TableCell>
                      {/* <TableCell sx={{ width: 160 }}>
                        <Box>
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              px: 1.5,
                              py: 0.5,
                              borderRadius: "16px",
                              fontSize: "0.70rem",
                              fontWeight: 600,
                              whiteSpace: "nowrap",
                              ...getStatusStyle(
                                row.calculated_status || "Pending",
                              ),
                            }}
                          >
                            {row.calculated_status || "Pending"}
                          </Box>

                          {row.tripsheet_number && (
                            <Typography
                              sx={{
                                display: "block",
                                mt: 0.4,
                                fontSize: "0.70rem",
                                color: "text.secondary",
                              }}
                            >
                              {row.tripsheet_number}
                            </Typography>
                          )}
                        </Box>
                      </TableCell> */}
                      <TableCell sx={{ width: 160 }}>
                        <Box>
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              px: 1.5,
                              py: 0.5,
                              borderRadius: "16px",
                              fontSize: "0.70rem",
                              fontWeight: 600,
                              whiteSpace: "nowrap",
                              ...getStatusStyle(
                                row.calculated_status || "Pending",
                              ),
                            }}
                          >
                            {row.calculated_status || "Pending"}
                          </Box>

                          {row.tripsheet_number && (
                            <Typography
                              component="button"
                              onClick={async () => {
                                try {
                                  console.log(
                                    "🚕 Opening Tripsheet:",
                                    row.tripsheetid,
                                  );

                                  if (!row.tripsheetid) {
                                    setSnackbar({
                                      open: true,
                                      msg: "Tripsheet ID is missing",
                                      severity: "error",
                                    });
                                    return;
                                  }

                                  // =====================================================
                                  // LOAD THE SAME COMPLETE DATA USED BY TRIPSHEET EDIT
                                  // =====================================================
                                  const res = await axiosInstance.get(
                                    `/api/tripsheets/${row.tripsheetid}`,
                                    {
                                      params: {
                                        client_id: clientId,
                                      },
                                    },
                                  );

                                  console.log(
                                    "✅ Complete Tripsheet Data:",
                                    res.data,
                                  );

                                  // =====================================================
                                  // OPEN TripsheetFormAdd WITH COMPLETE editData
                                  // =====================================================
                                  navigate("/tripsheets/tripsheet/add", {
                                    state: {
                                      editData: res.data,
                                      tripsheetId: row.tripsheetid,
                                      bookingId: row.bookingid,
                                      fromBooking: true,
                                    },
                                  });
                                } catch (error) {
                                  console.error(
                                    "❌ Failed to load Tripsheet:",
                                    error?.response?.data || error,
                                  );

                                  setSnackbar({
                                    open: true,
                                    msg:
                                      error?.response?.data?.message ||
                                      "Failed to fetch tripsheet details",
                                    severity: "error",
                                  });
                                }
                              }}
                              sx={{
                                display: "block",
                                mt: 0.4,
                                p: 0,
                                border: 0,
                                background: "none",
                                cursor: "pointer",
                                fontSize: "0.70rem",
                                color: "primary.main",
                                textDecoration: "underline",
                                fontFamily: "inherit",
                              }}
                            >
                              {row.tripsheet_number}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          width: 70,
                          textAlign: "center",
                          fontSize: "0.80rem",
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={(e) => handleRowMenuOpen(e, row)}
                          title="Actions"
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    No bookings to display.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={bookings.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
            labelRowsPerPage="Rows per page"
            labelDisplayedRows={({ from, to, count }) =>
              `Showing ${from}-${to} of ${count}`
            }
            sx={{
              ".MuiTablePagination-toolbar": { minHeight: 36, height: 36 },
              ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
                {
                  fontSize: 12,
                },
            }}
          />
        </Paper>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleRowMenuClose}
      >
        {/* Edit */}
        <MenuItem onClick={() => handleEdit(menuRow)} sx={{ fontSize: 13 }}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>

        {/* Generate Tripsheet */}
        {menuRow && menuRow.calculated_status === "Pending" && (
          <MenuItem
            onClick={() => handleGenerateTripsheet(menuRow)}
            sx={{ fontSize: 13 }}
          >
            <DirectionsCarIcon fontSize="small" sx={{ mr: 1 }} />
            Generate Tripsheet
          </MenuItem>
        )}

        {/* Cancel Booking */}
        {menuRow &&
          menuRow.calculated_status !== "Cancelled" &&
          menuRow.calculated_status !== "Processing" &&
          menuRow.calculated_status !== "Completed" && (
            <MenuItem
              onClick={() => handleCancelBooking(menuRow)}
              sx={{
                fontSize: 13,
                color: "text.secondary",
              }}
            >
              <CancelIcon
                fontSize="small"
                sx={{
                  mr: 1,
                  color: "text.secondary",
                }}
              />
              Cancel
            </MenuItem>
          )}
        {/* Delete */}
        <MenuItem
          onClick={() => handleDelete(menuRow)}
          sx={{
            fontSize: 13,
            color: "error.main",
          }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{
            width: "100%",
            fontSize: "0.95rem",
            fontWeight: 500,
            borderRadius: "8px",
            boxShadow: "0px 3px 8px rgba(0,0,0,0.25)",
            color: "#fff",
            "& .MuiAlert-icon": { color: "#fff" },
            ...(snackbar.severity === "error" && {
              backgroundColor: "#fc0707ff",
            }),
            ...(snackbar.severity === "success" && {
              backgroundColor: "#04710cff",
            }),
          }}
        >
          {snackbar.msg}
        </Alert>
      </Snackbar>

      {/* Confirmation Dialog */}
      <ConfirmationDialogComponent />
    </Box>
  );
}
